import { BookingLeadRecord } from "@/hooks/booking/useBookingLeads";
import type { CrmLead } from "@/types/crm";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const sanitizeDateString = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (DATE_ONLY_PATTERN.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  const fallback = trimmed.slice(0, 10);
  return DATE_ONLY_PATTERN.test(fallback) ? fallback : null;
};

const buildEventWindow = (metadata: Record<string, unknown>): { start: string; end: string } | null => {
  const windowValue = metadata["event_window"];
  if (windowValue) {
    const maybe =
      typeof windowValue === "string"
        ? (() => {
            try {
              const parsed = JSON.parse(windowValue);
              return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
            } catch {
              return null;
            }
          })()
        : typeof windowValue === "object"
          ? (windowValue as Record<string, unknown>)
          : null;
    if (maybe) {
      const start =
        sanitizeDateString(maybe["start"] as string | null) ?? sanitizeDateString(maybe["begin"] as string | null);
      const end =
        sanitizeDateString(maybe["end"] as string | null) ??
        sanitizeDateString(maybe["finish"] as string | null) ??
        start;
      if (start && end) {
        return { start, end };
      }
    }
  }
  const start = sanitizeDateString(metadata["window_start"] as string | null);
  const end = sanitizeDateString(metadata["window_end"] as string | null) ?? start;
  if (start) return { start, end: end ?? start };
  const routingStart = sanitizeDateString(metadata["routing_window_start"] as string | null);
  const routingEnd = sanitizeDateString(metadata["routing_window_end"] as string | null) ?? routingStart;
  if (routingStart) return { start: routingStart, end: routingEnd ?? routingStart };
  const target = sanitizeDateString(metadata["target_date"] as string | null);
  if (target) {
    return { start: target, end: target };
  }
  return null;
};

export function getEventWindowFromMetadata(
  metadata?: Record<string, unknown> | null,
): { start: string; end: string } | null {
  if (!metadata) return null;
  return buildEventWindow(metadata);
}

const deriveRegion = (metadata: Record<string, unknown>): string | undefined => {
  const city = metadata["city"] as string | undefined;
  const state = (metadata["state"] ?? metadata["region"]) as string | undefined;
  if (city && state) {
    return `${city}, ${state}`;
  }
  return state || city;
};

const priorityFromScore = (score?: number | null): number => {
  if (typeof score !== "number" || Number.isNaN(score)) {
    return 2;
  }
  if (score >= 0.75) return 3;
  if (score >= 0.5) return 2;
  return 1;
};

const normalizeMetadata = (record: BookingLeadRecord): Record<string, unknown> => {
  const base = { ...(record.lead ?? {}) };
  base.score = record.score;
  base.job_id = record.job_id;
  base.lead_id = record.lead_id;
  return base;
};

export function mapBookingLeadToCrmLead(record: BookingLeadRecord): CrmLead {
  const metadata = normalizeMetadata(record);
  const title =
    (metadata["venue_name"] as string | undefined) ||
    (metadata["company_name"] as string | undefined) ||
    (metadata["contact_name"] as string | undefined) ||
    record.lead_id ||
    `Lead ${record.id}`;
  const region = deriveRegion(metadata);
  const pipelineStage =
    (metadata["pipeline_stage"] as string | undefined) ||
    (metadata["stage"] as string | undefined) ||
    record.status ||
    "prospecting";
  const agentOwner = (metadata["agent_owner"] as string | undefined) ||
    (metadata["assigned_agent"] as string | undefined);
  const description =
    (metadata["description"] as string | undefined) ||
    (metadata["notes"] as string | undefined) ||
    record.rationale;
  const eventWindow = buildEventWindow(metadata);
  return {
    id: record.id,
    contact_id:
      typeof metadata["crm_contact_id"] === "number"
        ? metadata["crm_contact_id"]
        : undefined,
    title,
    source: (metadata["source"] as string | undefined) ?? "Booking AI",
    status: record.status ?? "pending",
    priority: priorityFromScore(Number(record.score ?? 0)),
    pipeline_stage: pipelineStage,
    agent_owner: agentOwner,
    region,
    event_window: eventWindow,
    description,
    metadata,
    created_at: record.created_at,
    updated_at: record.updated_at,
  };
}

export function getBookingLeadContact(record: BookingLeadRecord) {
  const metadata = record.lead ?? {};
  const name =
    (metadata["contact_name"] as string | undefined) ||
    (metadata["company_name"] as string | undefined) ||
    (metadata["venue_name"] as string | undefined);
  const region = deriveRegion(metadata);
  const tags = Array.isArray(metadata["tags"]) ? metadata["tags"].map(String) : [];
  return {
    id:
      typeof metadata["crm_contact_id"] === "number"
        ? metadata["crm_contact_id"]
        : undefined,
    name,
    company_name: metadata["company_name"] as string | undefined,
    email: metadata["contact_email"] as string | undefined,
    phone: metadata["phone"] as string | undefined,
    city: metadata["city"] as string | undefined,
    state: metadata["state"] as string | undefined,
    region,
    country: metadata["country"] as string | undefined,
    tags,
    source_urls: Array.isArray(metadata["source_urls"]) ? metadata["source_urls"].map(String) : [],
    social_links: Array.isArray(metadata["social_links"]) ? metadata["social_links"].map(String) : [],
  };
}

/**
 * Detail route for a Booking card. Booking records and CRM leads have separate ids;
 * Discover-origin records carry the CRM lead id they were created with.
 */
export function leadDetailPath(lead: Pick<CrmLead, "id" | "metadata">): string {
  const crmLeadId = (lead.metadata ?? {})["crm_lead_id"];
  const id = typeof crmLeadId === "number" || (typeof crmLeadId === "string" && /^\d+$/.test(crmLeadId)) ? crmLeadId : lead.id;
  return `/contacts/leads/${id}`;
}

/** Venue name + "City, ST". Pitch-created CRM lead titles read "Venue — City, ST". */
export function venueNameAndLocation(lead: Pick<CrmLead, "title" | "region">): { name: string; location: string | null } {
  const title = lead.title ?? "Untitled opportunity";
  const [name, titleLocation] = title.split(" — ");
  if (titleLocation) return { name, location: titleLocation };
  return { name: title, location: lead.region ?? null };
}

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/**
 * Create Event defaults from the Booking record's stored fields only.
 * City, state/region and country stay separate (never the combined "City, ST" label),
 * and the contact is a real person only: no fallback to the venue/company name.
 */
export function eventFormDefaults(record: BookingLeadRecord) {
  const metadata = (record.lead ?? {}) as Record<string, unknown>;
  const venue = text(metadata["venue_name"]) ?? text(metadata["company_name"]) ?? "TBD venue";
  const crmLeadId = Number(metadata["crm_lead_id"]);
  return {
    event_name: venue,
    venue,
    city: text(metadata["city"]),
    state: text(metadata["state"]) ?? text(metadata["region"]),
    country: text(metadata["country"]),
    contact_name: text(metadata["contact_name"]),
    contact_email: text(metadata["contact_email"]),
    booking_lead_id: record.id,
    metadata: {
      source: "booking_lead_stage",
      crm_lead_id: Number.isFinite(crmLeadId) && crmLeadId > 0 ? crmLeadId : undefined,
      global_venue_id: text(metadata["global_venue_id"]),
    },
  };
}

/** Readable message for Booking API errors (domain errors, plan entitlement, fallback). */
export function bookingErrorMessage(error: unknown, fallback: string): string {
  const err = error as { message?: string; payload?: { detail?: unknown } } | null;
  const detail = err?.payload?.detail;
  if (detail && typeof detail === "object") {
    const value = detail as { error?: string; message?: string };
    if (value.error === "requires_pro") return "Creating shows needs an active plan. Choose a plan in Settings → Billing.";
    if (value.message) return value.message;
  }
  if (typeof detail === "string" && detail) return detail;
  return err?.message || fallback;
}

/** Booked with a created show: the opportunity is historical and cannot move backward. */
export function bookingHasShow(lead: Pick<CrmLead, "metadata">): boolean {
  return Boolean((lead.metadata ?? {})["event_id"]);
}
